import type { SearchAutocompleteResponseDatagramBuf } from "~/classes/generated/response"
import type { SearchAutocompleteRequestDatagram } from "~/models/polyschema/SearchAutocompleteRequestDatagram"
import type { SearchAutocompleteResponseDatagram } from "~/models/polyschema/SearchAutocompleteResponseDatagram"

import { StaticFile } from "~/classes/file/StaticFile"
import { DatagramBuf } from "~/classes/generated/datagram"
import { RequestDatagramBuf, SearchAutocompleteRequestDatagramBuf } from "~/classes/generated/request"
import { DatagramBufferBuilder } from "~/utils/flatbuffers/builder"
import { addRequestIdToBuf, parseDatagramError, parseRequestId, parseVector } from "~/utils/flatbuffers/datagram"
import { addEntityPropertySelectionToBuf } from "~/utils/flatbuffers/entity"
import { parseFile } from "~/utils/flatbuffers/file"
import { addFileIdToBuf } from "~/utils/flatbuffers/id"
import { addSearchDefinitionToBuf } from "~/utils/flatbuffers/search"
import { maybe } from "~/utils/helpers"

const console = useLogger("file/find", theme.colors.teal.hex)

export function sendSearchAutocompleteRequest(request: SearchAutocompleteRequestDatagram) {
  const { sendWorkerDatagramBuf } = useCache()

  const fbb = new DatagramBufferBuilder(64)
  const locationBuffers = request.locations.map(location => addFileIdToBuf(fbb, location))
  const locations = SearchAutocompleteRequestDatagramBuf.createLocationsVector(fbb, locationBuffers)
  const requestId = addRequestIdToBuf(fbb, request.requestId)
  const definition = addSearchDefinitionToBuf(fbb, request.definition)
  const fileSelection = addEntityPropertySelectionToBuf(fbb, request.fileSelection)
  const pathSelection = addEntityPropertySelectionToBuf(fbb, request.pathSelection)
  SearchAutocompleteRequestDatagramBuf.startSearchAutocompleteRequestDatagramBuf(fbb)
  SearchAutocompleteRequestDatagramBuf.addRequestId(fbb, requestId)
  SearchAutocompleteRequestDatagramBuf.addOffline(fbb, request.offline)
  SearchAutocompleteRequestDatagramBuf.addDefinition(fbb, definition)
  SearchAutocompleteRequestDatagramBuf.addLimit(fbb, request.limit)
  SearchAutocompleteRequestDatagramBuf.addFileSelection(fbb, fileSelection)
  SearchAutocompleteRequestDatagramBuf.addPathSelection(fbb, pathSelection)
  SearchAutocompleteRequestDatagramBuf.addLocations(fbb, locations)

  const buf = SearchAutocompleteRequestDatagramBuf.endSearchAutocompleteRequestDatagramBuf(fbb)
  RequestDatagramBuf.startRequestDatagramBuf(fbb)
  RequestDatagramBuf.addSearchAutocompleteRequest(fbb, buf)

  const requests = DatagramBuf.createRequestVector(fbb, [RequestDatagramBuf.endRequestDatagramBuf(fbb)])
  DatagramBuf.startDatagramBuf(fbb)
  DatagramBuf.addRequest(fbb, requests)
  fbb.finish(DatagramBuf.endDatagramBuf(fbb))
  sendWorkerDatagramBuf(fbb.asUint8Array())
}

export function handleSearchAutocompleteResponseDatagramBuf(datagram: SearchAutocompleteResponseDatagramBuf) {
  onSearchAutocompleteResponse({
    requestId: parseRequestId(datagram.requestId()!),
    error: maybe(parseDatagramError)(datagram.error()),
    files: isDefined(datagram.error()) ? undefined : parseVector(n => parseFile(datagram.files(n)!), datagram.filesLength()),
    offline: datagram.offline(),
  })
}

export function onSearchAutocompleteResponse(response: SearchAutocompleteResponseDatagram) {
  const { emitInterfaceEvent, emitEntityEvent } = useEvents()

  emitInterfaceEvent("searchAutocompleteResponse", response, response.requestId)

  if (response.error || !response.files)
    return onSearchAutocompleteErrored(response.error!)

  const files = response.files.map(file => new StaticFile(file))

  for (const iFile of files) {
    emitEntityEvent("fileProperties", iFile.id, iFile.inner(), response.offline)
    const parentId = iFile.inferredParentId()
    if (!parentId)
      continue
    else
      emitEntityEvent("childProperties", parentId, iFile.inner(), response.offline)
  }
}

export function onSearchAutocompleteErrored(error: DatagramError) {
  console.error("Search autocomplete errored", error)
}
